/* ==========================================================================
   Saved Scripts Page (PORTAL-61)
   Provider view of prescriptions saved for later. Clicking a row re-opens the
   prescription form with all saved data prefilled so the provider can finish,
   sign, and submit (or save again).
   ========================================================================== */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageContainer } from '@/components/layout/PageContainer'
import { ProviderTabBar } from '@/components/layout/ProviderTabBar'
import { ProviderPageHeader } from '@/components/layout/ProviderPageHeader'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { usePortalSlug } from '@/context/PortalContext'
import { useToast } from '@/context/ToastContext'
import { listDrafts, getDraft, deleteDraft } from '@/api/portal'
import type { PrescriptionDraftListItem } from '@/api/types'
import { BookmarkX, PenLine, Trash2, RefreshCw, Loader2 } from 'lucide-react'

/* --------------------------------------------------------------------------
   Helpers
   -------------------------------------------------------------------------- */

function formatDateTime(isoString: string): string {
  const date = new Date(isoString)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function TableSkeleton() {
  return (
    <div className="animate-pulse space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="h-12 bg-[var(--color-gray-100)] rounded" />
      ))}
    </div>
  )
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function SavedScripts() {
  const slug = usePortalSlug()
  const navigate = useNavigate()
  const { error: showError, success: showSuccess } = useToast()

  const [drafts, setDrafts] = useState<PrescriptionDraftListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [continuingId, setContinuingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const fetchDrafts = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await listDrafts(slug)
      setDrafts(data.drafts)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load saved scripts.'
      )
    } finally {
      setLoading(false)
    }
  }, [slug])

  useEffect(() => {
    fetchDrafts()
  }, [fetchDrafts])

  // Open the form prefilled with the saved data so the provider can finish it.
  const handleContinue = useCallback(
    async (draftId: string) => {
      setContinuingId(draftId)
      try {
        const detail = await getDraft(slug, draftId)
        navigate(`/rx/${slug}/prescribe`, {
          state: { draftPrefill: { draftId, payload: detail.payload } },
        })
      } catch (err) {
        showError(
          err instanceof Error ? err.message : 'Failed to open saved script.'
        )
        setContinuingId(null)
      }
    },
    [slug, navigate, showError]
  )

  const handleDelete = useCallback(
    async (draftId: string) => {
      if (!window.confirm('Delete this saved script? This cannot be undone.')) {
        return
      }
      setDeletingId(draftId)
      try {
        await deleteDraft(slug, draftId)
        setDrafts((prev) => prev.filter((d) => d.id !== draftId))
        showSuccess('Saved script deleted.')
      } catch (err) {
        showError(
          err instanceof Error ? err.message : 'Failed to delete saved script.'
        )
      } finally {
        setDeletingId(null)
      }
    },
    [slug, showError, showSuccess]
  )

  return (
    <PageContainer size="md" className="py-8">
      <ProviderPageHeader />
      <ProviderTabBar />

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-charcoal)]">
            Saved Scripts
          </h1>
          <p className="text-sm text-[var(--color-gray-500)] mt-1">
            Prescriptions saved for later. Open one to finish, sign, and submit.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchDrafts}
          disabled={loading}
          aria-label="Refresh"
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>

      {loading ? (
        <Card>
          <TableSkeleton />
        </Card>
      ) : error ? (
        <Card>
          <div className="py-8 text-center">
            <p className="text-[var(--color-error)] mb-4">{error}</p>
            <Button variant="secondary" size="sm" onClick={fetchDrafts}>
              Try Again
            </Button>
          </div>
        </Card>
      ) : drafts.length === 0 ? (
        <Card>
          <div className="py-12 text-center">
            <BookmarkX className="h-12 w-12 text-[var(--color-gray-400)] mx-auto mb-3" />
            <p className="text-[var(--color-gray-600)] font-medium">
              No saved scripts
            </p>
            <p className="text-sm text-[var(--color-gray-500)] mt-1">
              Use “Save for later” on the New Prescription form to save a script
              here without submitting it.
            </p>
          </div>
        </Card>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block">
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[var(--color-gray-200)]">
                      <th className="text-left text-xs font-medium text-[var(--color-gray-500)] uppercase tracking-wider py-3 px-4">
                        Patient
                      </th>
                      <th className="text-left text-xs font-medium text-[var(--color-gray-500)] uppercase tracking-wider py-3 px-4">
                        Products
                      </th>
                      <th className="text-left text-xs font-medium text-[var(--color-gray-500)] uppercase tracking-wider py-3 px-4">
                        Last saved
                      </th>
                      <th className="text-right text-xs font-medium text-[var(--color-gray-500)] uppercase tracking-wider py-3 px-4">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-gray-100)]">
                    {drafts.map((draft) => (
                      <tr
                        key={draft.id}
                        role="button"
                        tabIndex={0}
                        className="hover:bg-[var(--color-gray-50)] transition-colors cursor-pointer"
                        onClick={() => handleContinue(draft.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            handleContinue(draft.id)
                          }
                        }}
                      >
                        <td className="py-3 px-4 text-sm text-[var(--color-charcoal)]">
                          {draft.patient_name || (
                            <span className="text-[var(--color-gray-400)]">
                              Unnamed patient
                            </span>
                          )}
                          {draft.has_signature && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--color-soft-lavender)] text-[var(--pharmacy-primary)]">
                              Signed
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-[var(--color-charcoal)]">
                          {draft.product_summary || (
                            <span className="text-[var(--color-gray-400)]">
                              No products
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-[var(--color-gray-500)]">
                          {formatDateTime(draft.updated_at)}
                        </td>
                        <td
                          className="py-3 px-4 text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleContinue(draft.id)}
                              disabled={continuingId === draft.id}
                            >
                              {continuingId === draft.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                              ) : (
                                <PenLine className="h-4 w-4 mr-1" aria-hidden="true" />
                              )}
                              Continue
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(draft.id)}
                              disabled={deletingId === draft.id}
                              className="text-[var(--color-gray-500)] hover:text-[var(--color-error)]"
                              aria-label="Delete saved script"
                            >
                              <Trash2 className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {drafts.map((draft) => (
              <Card key={draft.id}>
                <div className="space-y-2">
                  <div className="flex items-start justify-between">
                    <p className="text-sm font-medium text-[var(--color-charcoal)]">
                      {draft.patient_name || 'Unnamed patient'}
                    </p>
                    {draft.has_signature && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--color-soft-lavender)] text-[var(--pharmacy-primary)]">
                        Signed
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[var(--color-gray-600)]">
                    {draft.product_summary || 'No products'}
                  </p>
                  <p className="text-xs text-[var(--color-gray-500)]">
                    Last saved {formatDateTime(draft.updated_at)}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleContinue(draft.id)}
                      disabled={continuingId === draft.id}
                      className="flex-1 justify-center"
                    >
                      {continuingId === draft.id ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" aria-hidden="true" />
                      ) : (
                        <PenLine className="h-4 w-4 mr-1" aria-hidden="true" />
                      )}
                      Continue
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(draft.id)}
                      disabled={deletingId === draft.id}
                      className="text-[var(--color-gray-500)] hover:text-[var(--color-error)]"
                      aria-label="Delete saved script"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </PageContainer>
  )
}

export default SavedScripts
